import axios from 'axios';
import Cookies from 'js-cookie';
import { BASE_URL_SANDBOX, BASE_URL_NODE } from '../Constants/Config';

/**
 * Attaches the customer's bearer token, and ONLY to endpoints that verify it (PHP
 * require_customer_auth, Node requireCustomerAuth), plus Orders/order_details and
 * Process_order_online/saveOrder, whose shadow-mode access logs read it. Every other request
 * goes out exactly as before: an Authorization header
 * makes the browser send a CORS preflight, and some public PHP controllers (e.g. Findstore)
 * don't allow that header. Keep this list in sync with those server-side guards.
 */
const CUSTOMER_TOKEN_PATHS = [
  [
    BASE_URL_SANDBOX,
    [
      'customer_account/',
      'available_balance_history_api/',
      'myorder_react/open',
      'myorder_react/close',
      'lp_sc_api/loyalty_program_history',
      'lp_sc_api/store_credit_history',
      'notificationsetting/getnotificationsettings',
      'notificationsetting/updatenotificationsettings',
      'findstorereact/deletesavedcard',
      'findstorereact/get_lp_sc_data',
      'findstorereact/calculate_loyality_points',
      'findstorereact/listwishlist',
      'findstorereact/savewishlist',
      'findstorereact/deletewishlistajax',
      'findstorereact/getcardlist',
      'onlinestorereactnewui/update_accounts_identification',
      'onlinestorereactnewui/update_customer_name',
      'onlinestorereactnewui/update_customer_phone',
      'onlinestorereactnewui/wishlist_home',
      'onlinestorereactnewui/buyitagain',
      'orders/order_details',
      'process_order_online/saveorder',
      'onlinestorereactnewui/save_password',
      'findstorereact/check_coupon',
    ],
  ],
  [BASE_URL_NODE, ['wishlist/', 'onlinestorereactnewui/buyitagainbymerchant']],
];

function needsCustomerToken(url) {
  if (typeof url !== 'string') return false;
  for (const [base, paths] of CUSTOMER_TOKEN_PATHS) {
    if (!base || !url.startsWith(base)) continue;
    const path = url.slice(base.length).split('?')[0].replace(/^\/+/, '').toLowerCase();
    return paths.some((p) => (p.endsWith('/') ? path.startsWith(p) : path === p || path.startsWith(`${p}/`)));
  }
  return false;
}

function isBlankBearer(value) {
  if (!value) return true;
  const m = /^Bearer\s*(.*)$/i.exec(String(value).trim());
  if (!m) return false;
  const token = m[1].trim();
  return token === '' || token.toLowerCase() === 'undefined' || token.toLowerCase() === 'null';
}

axios.interceptors.request.use((config) => {
  try {
    if (!needsCustomerToken(config.url)) {
      return config;
    }

    // Signed out: every logout path removes userLoginData but not login_token. Drop the
    // leftover token rather than attach a previous customer's session to this request.
    if (!Cookies.get('userLoginData')) {
      if (Cookies.get('login_token')) {
        Cookies.remove('login_token', { path: '/' });
      }
      return config;
    }

    const existing = config.headers && (config.headers.Authorization || config.headers.authorization);
    if (existing && !isBlankBearer(existing)) {
      return config;
    }

    const token = Cookies.get('login_token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // Never let interceptor logic itself block a request from going out.
  }
  return config;
});

/**
 * An expired or invalid customer session must end the storefront session, not leave
 * `userLoginData` saying "signed in" while every account call fails. PHP answers HTTP 200
 * with `{status: 401|403, message}` (require_customer_auth); Node answers HTTP 401
 * (requireCustomerAuth). Matched on those exact messages so an unrelated business 401/403
 * never signs anyone out. Only a visitor who is currently signed in (userLoginData present) is
 * signed out; a signed-out visitor whose page still calls a guarded endpoint just gets the
 * failure back, so the reload can never repeat.
 */
const CUSTOMER_AUTH_FAILURE_MESSAGES = [
  'Unauthorized. Invalid or expired token.',
  'Access denied. You can only access your own data.',
  'Unauthorized. Please sign in again.',
];

// qv_send_unauthorized(): the server-wide body PHP sends with an HTTP 401. require_customer_auth
// sends it for an invalid or expired customer token, but Orders/order_details also sends it when a
// signed-in customer opens an order that is not theirs, so there it does not mean the session is over.
const SERVER_AUTH_401_MESSAGE = 'Unauthorized - Invalid token or mismatched credential';

function isOrderDetailsUrl(url) {
  return Boolean(BASE_URL_SANDBOX) && url.startsWith(BASE_URL_SANDBOX)
    && /^\/*orders\/order_details(?:[/?]|$)/i.test(url.slice(BASE_URL_SANDBOX.length));
}

let endingSession = false;

function endCustomerSession() {
  if (endingSession || !Cookies.get('userLoginData')) return;
  endingSession = true;
  ['login_token', 'userLoginData', 'is_guest'].forEach((name) => Cookies.remove(name, { path: '/' }));
  window.location.reload();
}

function isCustomerAuthFailure(config, message) {
  if (!needsCustomerToken(config?.url)) return false;
  if (message === SERVER_AUTH_401_MESSAGE) return !isOrderDetailsUrl(config.url);
  return CUSTOMER_AUTH_FAILURE_MESSAGES.includes(message);
}

axios.interceptors.response.use(
  (response) => {
    try {
      const status = response.data?.status;
      if ((status === 401 || status === 403) && isCustomerAuthFailure(response.config, response.data?.message)) {
        endCustomerSession();
      }
    } catch (e) {
      // Never let interceptor logic itself throw on the caller.
    }
    return response;
  },
  (error) => {
    try {
      if (error?.response?.status === 401 && isCustomerAuthFailure(error.config, error.response.data?.message)) {
        endCustomerSession();
      }
    } catch (e) {
      // Never let interceptor logic itself throw on the caller.
    }
    return Promise.reject(error);
  },
);
